import 'dayjs/plugin/timezone';
import 'dayjs/plugin/utc';
import dayjs, { type Dayjs } from 'dayjs';

/** An object that combines two dates to represent a time interval. */
export interface Interval {
  start: Date;
  end: Date;
}

/** An object that combines two Dayjs objects to represent a time interval. */
export interface DayjsInterval {
  start: Dayjs;
  end: Dayjs;
}

/**
 * Minimal subset of dashing-ui's `DateService` consumed by these helpers.
 * A full `DateService` (from `useDateService()`) is structurally assignable to it.
 */
export interface TimezoneDateService {
  readonly timezone: string;
  now: () => Dayjs;
  fromUtc: (date: dayjs.ConfigType) => Dayjs;
  toUtc: (date: dayjs.ConfigType) => Dayjs;
  format: (date: dayjs.ConfigType, formatStr?: string) => string;
  formatUtc: (date: dayjs.ConfigType, formatStr?: string) => string;
}

/**
 * The part of a `DateService` these helpers read. Only `timezone`: dashing-ui deprecates its
 * dayjs methods (`now`, `fromUtc`, ...) and drops them next major.
 */
export type DateServiceParam = Pick<TimezoneDateService, 'timezone'>;

/**
 * An object that combines a preset and a date range.
 * Corresponds to the `PresetType` defined in dashing-ui.
 */
export interface PresetType {
  value: string;
  label: string;
  range?: (options?: { dateService?: DateServiceParam }) => Interval | null;
}

/**
 * An object that combines a preset and a date range.
 * Corresponds to the `DateRangeModel` defined in dashing-ui.
 */
export interface DateRangeModel {
  preset: string;
  presetObject: PresetType;
  range: Interval | null;
}

/**
 * Create a DateService instance for timezone-aware date operations.
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 */
export function createDateService(timezone: string): TimezoneDateService {
  return {
    get timezone() {
      return timezone;
    },
    now: () => dayjs.tz(undefined, timezone),
    fromUtc: (date) => dayjs.utc(date).tz(timezone),
    toUtc: (date) => dayjs.tz(date, timezone).utc(),
    format: (date, formatStr) => dayjs.tz(date, timezone).format(formatStr),
    formatUtc: (date, formatStr) => dayjs.utc(date).tz(timezone).format(formatStr),
  };
}

export const FAR_FUTURE = new Date(8640000000000000);
export const DISTANT_PAST = new Date(-8640000000000000);
export const ALL_TIME: Readonly<Interval> = Object.freeze({ start: DISTANT_PAST, end: FAR_FUTURE });

/**
 * Convert date string from a given format to another format.
 * i.e., reformatDateString('2000-03-15', 'YYYY-MM-DD', 'MMMM D, YYYY') = 'March 15, 2000'
 * @param dateString - The original date string.
 * @param inputDateFormat - dayjs format code for the original date string.
 * @param targetDateFormat - dayjs format code for the target output.
 * - For more format codes, check: https://day.js.org/docs/en/display/format
 */
export function reformatDateString(
  dateString: dayjs.ConfigType,
  inputDateFormat: string,
  targetDateFormat: string,
): string {
  const parsed = dayjs(dateString, inputDateFormat);
  // Fall back to lenient parsing if strict format fails (e.g. '2021-4-11' with 'YYYY-MM-DD')
  return (parsed.isValid() ? parsed : dayjs(dateString)).format(targetDateFormat);
}

/**
 * Get a merged date range string.
 * @param startDateString - Start date in YYYY-MM-DD format. 2021-01-01
 * @param endDateString - End date in YYYY-MM-DD format.
 * @returns i.e., 'June 11 - June 12, 2021'
 */
export function getMergedDateRange(
  startDateString: dayjs.ConfigType,
  endDateString: dayjs.ConfigType,
): string {
  let formattedStart = reformatDateString(startDateString, 'YYYY-MM-DD', 'MMMM D, YYYY');
  const formattedEnd = reformatDateString(endDateString, 'YYYY-MM-DD', 'MMMM D, YYYY');
  // Merge common year if start and end has the same value
  if (formattedStart.split(', ')[1] === formattedEnd.split(', ')[1]) {
    [formattedStart] = formattedStart.split(', ');
  }
  return `${formattedStart} - ${formattedEnd}`;
}

/**
 * Get days interval between two dates.
 * @param startDate - Start date in YYYY-MM-DD format. 2021-01-01
 * @param endDate - End date in YYYY-MM-DD format.
 * @returns i.e., 3
 */
export function differenceBetweenDays(
  startDate: dayjs.ConfigType,
  endDate: dayjs.ConfigType,
): number {
  // Coerce null/undefined to 0 (epoch) to match date-fns behavior where new Date(null) = epoch
  const end = dayjs(endDate ?? 0);
  const start = dayjs(startDate ?? 0);
  return end.startOf('day').diff(start.startOf('day'), 'day');
}

/**
 * Return the formatted date string in the given format.
 * @param pattern - dayjs format string (https://day.js.org/docs/en/display/format)
 */
export function formatDate(date: dayjs.ConfigType, pattern: string): string {
  return dayjs(date).format(pattern);
}

/**
 * Parse a value against an explicit input format, e.g. an API's `YYYY-MM-DD` day strings.
 *
 * The value MUST match `format`; a mismatch yields an invalid date rather than a best guess.
 *
 * Honouring `format` needs dayjs's `customParseFormat` plugin, which the app extends globally at
 * startup rather than here — this module does not extend it, and neither did the graph util this
 * moved from.
 */
export function parseDateWithFormat(value: dayjs.ConfigType, format = 'YYYY-MM-DD'): dayjs.Dayjs {
  return dayjs(value, format);
}

/** Check if date is within interval. */
export const isWithinInterval = (
  date: Date,
  interval: Interval | false | null | undefined,
): boolean => !!interval && date >= interval.start && date <= interval.end;

/** Check if intervalOne is contained within intervalTwo. */
export const isIntervalWithinInterval = (intervalOne: Interval, intervalTwo: Interval): boolean =>
  isWithinInterval(intervalOne.start, intervalTwo) &&
  isWithinInterval(intervalOne.end, intervalTwo);

/** Get date interval from start of day to end of day. */
export const getDayAsInterval = (date: dayjs.ConfigType): Interval => ({
  start: dayjs(date).startOf('day').toDate(),
  end: dayjs(date).endOf('day').toDate(),
});

/** Returns true if date is both a Date object and a valid Date. */
export const isValidDate = (date: unknown): date is Date =>
  date instanceof Date && !Number.isNaN(date.getTime());

export const isValidDateString = (date: string): boolean => {
  const dateValue = Date.parse(date);
  return isValidDate(new Date(dateValue));
};

/**
 * Attempts to parse isoString into a Date. If it cannot be successfully parsed,
 * returns fallbackDate instead.
 */
export const parseISOOrElse = (isoString: string | null | undefined, fallbackDate: Date): Date => {
  if (isoString == null) return fallbackDate;
  const parsed = dayjs(isoString);
  return parsed.isValid() ? parsed.toDate() : fallbackDate;
};

/** Returns true if interval is valid. */
export const isValidInterval = (interval?: Partial<Interval> | null): boolean =>
  isValidDate(interval?.start) &&
  isValidDate(interval?.end) &&
  (dayjs(interval?.start).isBefore(interval?.end) || dayjs(interval?.start).isSame(interval?.end));

/** Returns date if date is within interval -- otherwise returns the nearest endpoint of interval. */
export const clamp = (date: Date, interval: Interval): Date => {
  if (dayjs(date).isBefore(interval.start)) return interval.start;
  if (dayjs(date).isAfter(interval.end)) return interval.end;
  return date;
};

/** Returns a list of dates for each day in a specified interval. */
export const eachDayOfInterval = (interval: Interval): Date[] => {
  let day = dayjs(interval.start).startOf('day');
  const dates: Date[] = [];
  while (!day.isAfter(interval.end)) {
    dates.push(day.toDate());
    day = day.add(1, 'day');
  }
  return dates;
};

/** Returns a list of dates for the start of each month in a specified interval. */
export const eachMonthOfInterval = (interval: Interval): Date[] => {
  let month = dayjs(interval.start).startOf('month');
  const dates: Date[] = [];
  while (!month.isAfter(interval.end)) {
    dates.push(month.toDate());
    month = month.add(1, 'month');
  }
  return dates;
};

/** Check if intervals overlap each other inclusively. */
export const areIntervalsOverlapping = (
  intervalOne: Interval | false | null | undefined,
  intervalTwo: Interval | false | null | undefined,
): boolean =>
  !!intervalOne &&
  !!intervalTwo &&
  (isWithinInterval(intervalOne.start, intervalTwo) ||
    isWithinInterval(intervalOne.end, intervalTwo) ||
    isWithinInterval(intervalTwo.start, intervalOne) ||
    isWithinInterval(intervalTwo.end, intervalOne));

function isRangeInCurrentYear(range: readonly dayjs.ConfigType[] | null | undefined): boolean {
  const today = dayjs();
  const start = dayjs(range?.[0], 'YYYY-MM-DD');
  const end = dayjs(range?.[1], 'YYYY-MM-DD');
  return today.isSame(start, 'year') && today.isSame(end, 'year');
}

export function bothRangesInCurrentYear(
  firstRange: readonly dayjs.ConfigType[] | null | undefined,
  secondRange: readonly dayjs.ConfigType[] | null | undefined,
): boolean {
  return isRangeInCurrentYear(firstRange) && isRangeInCurrentYear(secondRange);
}

export function isSameRange(
  firstRange: readonly dayjs.ConfigType[] | null | undefined,
  secondRange: readonly dayjs.ConfigType[] | null | undefined,
): boolean {
  const firstStartDate = dayjs(firstRange?.[0], 'YYYY-MM-DD');
  const firstEndDate = dayjs(firstRange?.[1], 'YYYY-MM-DD');
  const secondStartDate = dayjs(secondRange?.[0], 'YYYY-MM-DD');
  const secondEndDate = dayjs(secondRange?.[1], 'YYYY-MM-DD');
  return firstStartDate.isSame(secondStartDate, 'day') && firstEndDate.isSame(secondEndDate, 'day');
}

/**
 * Returns yesterday's date, computed fresh on each call.
 * Use this instead of caching `new Date()` in a computed or prop default
 * to avoid stale dates when a browser tab stays open overnight.
 */
export function getYesterday(): Date {
  const today = new Date();
  return new Date(today.setDate(today.getDate() - 1));
}

export function isSameDateRange(
  firstRange: readonly dayjs.ConfigType[] | null | undefined,
  secondRange: readonly dayjs.ConfigType[] | null | undefined,
): boolean {
  const firstStartDate = dayjs(firstRange?.[0]);
  const firstEndDate = dayjs(firstRange?.[1]);
  const secondStartDate = dayjs(secondRange?.[0]);
  const secondEndDate = dayjs(secondRange?.[1]);
  return firstStartDate.isSame(secondStartDate, 'day') && firstEndDate.isSame(secondEndDate, 'day');
}

/** Find the max of a list of dates. */
const maxDates = (...dates: Date[]): Date =>
  dates.reduce((maxSoFar, d) => (d > maxSoFar ? d : maxSoFar), DISTANT_PAST);

/** Find the min of a list of dates. */
const minDates = (...dates: Date[]): Date =>
  dates.reduce((minSoFar, d) => (d < minSoFar ? d : minSoFar), FAR_FUTURE);

/** Find the interval consisting of dates which fall within all provided intervals. */
export const intersectIntervals = (...intervals: (Interval | false)[]): Interval | false => {
  const valid = intervals.filter((interval): interval is Interval => Boolean(interval));
  if (valid.length !== intervals.length) {
    return false;
  }
  const start = maxDates(...valid.map((interval) => interval.start));
  const end = minDates(...valid.map((interval) => interval.end));
  return end < start ? false : { start, end };
};

/** Find the duration of a date interval in milliseconds. */
export const getIntervalMillisecondDuration = (
  interval: Interval | false | null | undefined,
): number => {
  if (!interval) {
    return 0;
  }
  return interval.end.getTime() - interval.start.getTime();
};

/** Return an interval including all future dates. */
export const getFutureAsInterval = (): Interval => ({ start: new Date(), end: FAR_FUTURE });

/** Return true if the interval is open-ended. */
export const isAllTime = (interval?: Interval | false | null): boolean =>
  !!interval && interval.start === DISTANT_PAST && interval.end === FAR_FUTURE;

/** Return an array of date strings equal or between a start and end date. */
export const generateStringsForDateRange = (
  startDate: dayjs.ConfigType,
  endDate: dayjs.ConfigType,
  dateFmt: string,
  timeUnit: dayjs.ManipulateType,
): string[] => {
  const dateRange: string[] = [];
  let currentDate = dayjs(startDate);
  while (!currentDate.isAfter(endDate, timeUnit)) {
    dateRange.push(currentDate.format(dateFmt));
    currentDate = currentDate.add(1, timeUnit);
  }
  return dateRange;
};

/** Parses a date string into a Date object. */
export function parseDate(value: string): Date {
  return dayjs(value, 'YYYY-MM-DD').toDate();
}

/** Returns a Date `value` days ago. */
export function daysAgo(value: number): Date {
  return dayjs().subtract(value, 'd').toDate();
}

/** Return date interval with a specified amount of time subtracted from the specified date. */
export function getDateRange(
  endDate: dayjs.ConfigType,
  subtractValue: number,
  unit: dayjs.ManipulateType = 'day',
  dateService?: DateServiceParam,
): Interval {
  const tz = dateService?.timezone;
  const djs = (date: dayjs.ConfigType) => (tz ? dayjs.tz(date, tz) : dayjs(date));

  const endDayJs = unit === 'day' ? djs(endDate) : djs(endDate).startOf(unit).subtract(1, 'day');
  const end = djs(endDayJs.toDate()).endOf('day').toDate();

  const offset = unit === 'day' ? subtractValue : subtractValue - 1;
  const startDayJs =
    subtractValue > 1 || unit !== 'day' ? djs(end).subtract(offset, unit) : djs(end);
  const start = startDayJs.startOf(unit).toDate();
  return { start, end };
}

/** Return date interval with a specified amount of time subtracted. */
export function lastDateRange(
  subtractValue = 1,
  unit: dayjs.ManipulateType = 'day',
  dateService?: DateServiceParam,
): Interval {
  return getDateRange(new Date(), subtractValue, unit, dateService);
}

/** Get a date range starting from a given number of days ago to today. */
export function getDateRangeOffsetFromToday(
  offsetDays: number,
  dateService?: DateServiceParam,
): DayjsInterval {
  const now = dateService ? dayjs().tz(dateService.timezone) : dayjs();
  return {
    start: now.subtract(offsetDays, 'd').startOf('day'),
    end: now.endOf('day'),
  };
}

/** Returns a date range spanning from start of first day to end of last day. */
export function getDateRangeFromDates(
  startDate: dayjs.ConfigType,
  endDate: dayjs.ConfigType,
  dateService?: DateServiceParam,
): DayjsInterval {
  const tz = dateService?.timezone;
  const djs = (date: dayjs.ConfigType) => (tz ? dayjs.tz(date, tz) : dayjs(date));
  return {
    start: djs(startDate).startOf('day'),
    end: djs(endDate).endOf('day'),
  };
}

/**
 * Convert a dashing-ui `PresetType` to a `DateRangeModel`.
 * Useful when you want to set the modelValue of a dashing-ui DateRangeDropdown to a preset.
 */
export function convertPresetToDateRangeModel(
  preset: PresetType,
  dateService?: DateServiceParam,
): DateRangeModel {
  return {
    preset: preset.value,
    presetObject: preset,
    range: preset.range?.({ dateService }) ?? null,
  };
}

/**
 * Find a dashing-ui `PresetType` by its value.
 * This function is copied from dashing-ui `date-ranges.ts` since it isn't currently exported.
 */
export function findPresetObject(
  presets: PresetType[],
  presetValue: string,
): PresetType | undefined {
  return presets.find((preset) => preset.value === presetValue);
}

/**
 * Converts a Dayjs object to a JavaScript Date object by extracting its
 * individual date and time components (year, month, day, etc.) and passing
 * them to the JS Date constructor.
 *
 * This effectively drops any timezone information from the Dayjs object
 * and creates a Date object that represents the same local clock time
 * in the system's local timezone — not the same moment in time.
 *
 * Why this function is useful:
 * - If you use `.toDate()` on a timezone-aware Dayjs object (e.g., one created with `.tz()`),
 *   the resulting Date will represent the exact point in time (in UTC), which can shift the
 *   visible date/time when rendered in local time (e.g., in a date picker).
 * - This function avoids that by copying just the visible values (Y/M/D/H/m/s/ms) into a new
 *   Date object, so it preserves the user's intended clock time, even if the actual moment
 *   in time changes.
 *
 * @param dayJsDate - The dayjs object to convert
 * @returns A JavaScript Date object with the same date/time values as the input
 *
 * @example
 * const dayjsDate = dayjs('2023-12-25 14:30:00').tz('Australia/Brisbane');
 * const jsDate = dayjsToDateIgnoreTimezone(dayjsDate);
 * // Returns a Date object representing December 25, 2023 at 2:30 PM, in local timezone
 */
export function dayjsToDateIgnoreTimezone(dayJsDate: Dayjs): Date {
  return new Date(
    dayJsDate.year(),
    dayJsDate.month(),
    dayJsDate.date(),
    dayJsDate.hour(),
    dayJsDate.minute(),
    dayJsDate.second(),
    dayJsDate.millisecond(),
  );
}

// ============================================================
// Wrapper exports (replacements for direct date-fns imports)
// ============================================================

/** Parse an ISO date string into a Date object. */
export function parseISODate(str: dayjs.ConfigType): Date {
  return dayjs(str).toDate();
}

/** Format a Date as an ISO 8601 string (with local timezone offset). */
export function formatISODate(date: dayjs.ConfigType): string {
  return dayjs(date).format();
}

/** Returns true if d1 is before d2. */
export function isDateBefore(d1: dayjs.ConfigType, d2: dayjs.ConfigType): boolean {
  return dayjs(d1).isBefore(d2);
}

/** Returns true if d1 is after d2. */
export function isDateAfter(d1: dayjs.ConfigType, d2: dayjs.ConfigType): boolean {
  return dayjs(d1).isAfter(d2);
}

/** Returns true if d1 and d2 represent the same moment in time. */
export function isDateEqual(d1: dayjs.ConfigType, d2: dayjs.ConfigType): boolean {
  return dayjs(d1).isSame(d2);
}

/** Returns true if d1 and d2 fall on the same calendar day. */
export function isSameDay(d1: dayjs.ConfigType, d2: dayjs.ConfigType): boolean {
  if (d1 == null || d2 == null) return false;
  return dayjs(d1).isSame(d2, 'day');
}

/** Returns true if d1 and d2 fall in the same calendar month. */
export function isSameMonth(d1: dayjs.ConfigType, d2: dayjs.ConfigType): boolean {
  if (d1 == null || d2 == null) return false;
  return dayjs(d1).isSame(d2, 'month');
}

/** Returns true if d1 and d2 fall in the same calendar year. */
export function isSameYear(d1: dayjs.ConfigType, d2: dayjs.ConfigType): boolean {
  if (d1 == null || d2 == null) return false;
  return dayjs(d1).isSame(d2, 'year');
}

/** Returns the 4-digit year of a date. */
export function getYear(date: dayjs.ConfigType): number {
  return dayjs(date).year();
}

/** Returns a Date set to the start of the given date's day (00:00:00.000). */
export function startOfDay(date: dayjs.ConfigType): Date {
  if (date == null) return new Date(NaN);
  return dayjs(date).startOf('day').toDate();
}

/** Returns a Date set to the end of the given date's day (23:59:59.999). */
export function endOfDay(date: dayjs.ConfigType): Date {
  if (date == null) return new Date(NaN);
  return dayjs(date).endOf('day').toDate();
}

/**
 * Returns the ISO string for the end (23:59:59) of the given date's calendar day
 * in UTC — the day's Y/M/D taken as-is, stamped at UTC 23:59:59.
 *
 * Pass a `YYYY-MM-DD` string when the day comes from a dashing-ui date component:
 * those Dates are anchored in the user's profile timezone, so reading their
 * browser-local Y/M/D would pick the wrong day. See `formatDateInUserTz`.
 */
export function endOfDayUtcISO(date: Date | string): string {
  if (typeof date === 'string') {
    const [year, month, day] = date.split('-').map(Number);
    // `formatDateInUserTz` yields the string 'Invalid Date' for unparseable input, which
    // would otherwise reach `toISOString()` as NaN and throw.
    if ([year, month, day].some(Number.isNaN)) return new Date(NaN).toString();
    return new Date(Date.UTC(year, month - 1, day, 23, 59, 59)).toISOString();
  }
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59),
  ).toISOString();
}

/** Add n days to a Date, returning a new Date. */
export function addDays(date: dayjs.ConfigType, n: number): Date {
  return dayjs(date).add(n, 'day').toDate();
}

/** Add n months to a Date, returning a new Date. */
export function addMonths(date: dayjs.ConfigType, n: number): Date {
  return dayjs(date).add(n, 'month').toDate();
}

/** Add n weeks to a Date, returning a new Date. */
export function addWeeks(date: dayjs.ConfigType, n: number): Date {
  return dayjs(date).add(n, 'week').toDate();
}

/** Add n hours to a Date, returning a new Date. */
export function addHours(date: dayjs.ConfigType, n: number): Date {
  return dayjs(date).add(n, 'hour').toDate();
}

/** Add n minutes to a Date, returning a new Date. */
export function addMinutes(date: dayjs.ConfigType, n: number): Date {
  return dayjs(date).add(n, 'minute').toDate();
}

/** Subtract n days from a Date, returning a new Date. */
export function subDays(date: dayjs.ConfigType, n: number): Date {
  return dayjs(date).subtract(n, 'day').toDate();
}

/** Subtract n weeks from a Date, returning a new Date. */
export function subWeeks(date: dayjs.ConfigType, n: number): Date {
  return dayjs(date).subtract(n, 'week').toDate();
}

/** Subtract n minutes from a Date, returning a new Date. */
export function subMinutes(date: dayjs.ConfigType, n: number): Date {
  return dayjs(date).subtract(n, 'minute').toDate();
}

/** Get the difference between d1 and d2 in whole hours. */
export function differenceInHours(d1: dayjs.ConfigType, d2: dayjs.ConfigType): number {
  return dayjs(d1).diff(d2, 'hour');
}

/** Get the difference between d1 and d2 in calendar days (ignoring time). */
export function differenceInCalendarDays(d1: dayjs.ConfigType, d2: dayjs.ConfigType): number {
  return dayjs(d1).startOf('day').diff(dayjs(d2).startOf('day'), 'day');
}

/** Get the difference between d1 and d2 in calendar months (ignoring day/time). */
export function differenceInCalendarMonths(d1: dayjs.ConfigType, d2: dayjs.ConfigType): number {
  return dayjs(d1).startOf('month').diff(dayjs(d2).startOf('month'), 'month');
}

/** Today, or `offset` days before it, as `YYYY-MM-DD`. */
export function getCurrentDate(offset = 0): string {
  return dayjs().subtract(offset, 'd').format('YYYY-MM-DD');
}
